import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Link2, Lock, Mail, Smartphone, Sparkles, UserPlus } from "lucide-react";

import {
  type AdminUserCreate,
  AdminUserCreateSchema,
  type AdminUserEdit,
  AdminUserEditSchema,
  canLinkFromError,
  passwordStrength,
} from "@/features/admin-users/domain/admin-user.schema";
import { InlineError } from "@/shared/components/data/ErrorState";
import { FormSheet } from "@/shared/components/data/FormSheet";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import {
  AccountKind,
  useCreateAdminUserMutation,
  useListRolesQuery,
  useUpdateAdminUserMutation,
} from "@/store/apis/adminApi.enhanced";
import type { AdminUser } from "@/store/apis/adminApi.enhanced";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  user?: AdminUser | null;
}

const FORM_ID = "admin-user-form";
type Mode = "new" | "link";

export function AdminUserFormSheet({ open, onOpenChange, user }: Props) {
  const isEdit = Boolean(user);
  if (isEdit) {
    return <EditSheet open={open} onOpenChange={onOpenChange} user={user!} />;
  }
  return <CreateSheet open={open} onOpenChange={onOpenChange} />;
}

/* ────────────────── Create ────────────────── */

function CreateSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const rolesQuery = useListRolesQuery(undefined, { skip: !open });
  const [createAdmin, createState] = useCreateAdminUserMutation();
  const [showPwd, setShowPwd] = useState(false);
  const [mode, setMode] = useState<Mode>("new");

  // Hint "esta cuenta ya existe en el app — ¿vincular?" surge del back
  // cuando el POST en modo `new` falla con 409 + details.can_link: true.
  const canLink = canLinkFromError(createState.error);

  const form = useForm<AdminUserCreate>({
    resolver: zodResolver(AdminUserCreateSchema),
    defaultValues: {
      mode: "new",
      email: "",
      full_name: "",
      role: "",
      password: "",
    } as AdminUserCreate,
    mode: "onBlur",
  });

  useEffect(() => {
    if (open) {
      form.reset({
        mode: "new",
        email: "",
        full_name: "",
        role: "",
        password: "",
      } as AdminUserCreate);
      setMode("new");
      setShowPwd(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const switchMode = (next: Mode) => {
    setMode(next);
    const common = {
      email: form.getValues("email"),
      full_name: form.getValues("full_name"),
      role: form.getValues("role"),
    };
    if (next === "link") {
      form.reset({ mode: "link", ...common } as AdminUserCreate);
    } else {
      form.reset({ mode: "new", ...common, password: "" } as AdminUserCreate);
    }
  };

  const password = (form.watch("password" as const) as string | undefined) ?? "";
  const strength = useMemo(() => passwordStrength(password), [password]);

  const onSubmit = form.handleSubmit(async (values) => {
    const body =
      values.mode === "link"
        ? {
            email: values.email,
            full_name: values.full_name,
            role: values.role,
            link_existing_account: true as const,
          }
        : {
            email: values.email,
            full_name: values.full_name,
            role: values.role,
            password: values.password,
          };
    try {
      const result = await createAdmin({ body }).unwrap();
      if (result.account_kind === AccountKind.Linked) {
        toast.success(`Cuenta vinculada: ${result.full_name} ya puede entrar al backoffice.`);
      } else {
        toast.success("Admin creado. Deberá enrolar MFA en su primer login.");
      }
      onOpenChange(false);
    } catch {
      // InlineError + CTA de vincular lo maneja la UI.
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={mode === "link" ? "Vincular cuenta del app" : "Nuevo admin"}
      description={
        mode === "link"
          ? "La usuaria ya tiene cuenta en el app móvil. Al vincular, mantiene su contraseña y accede al backoffice con el mismo correo."
          : "Define las credenciales iniciales. El admin deberá enrolar MFA en su primer login."
      }
      formId={FORM_ID}
      submitLabel={mode === "link" ? "Vincular cuenta" : "Crear admin"}
      submitting={createState.isLoading}
      dirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form id={FORM_ID} onSubmit={onSubmit} className="space-y-5" noValidate>
          {/* Mode switch — segmented control */}
          <ModeSwitch mode={mode} onChange={switchMode} />

          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Nombre completo
                </FormLabel>
                <FormControl>
                  <Input
                    autoComplete="off"
                    placeholder="Ej. María García"
                    className="h-11 text-[13.5px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Correo electrónico
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail
                      aria-hidden
                      className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    />
                    <Input
                      type="email"
                      autoComplete="off"
                      inputMode="email"
                      placeholder={mode === "link" ? "usuaria@correo.com" : "admin@arya.pe"}
                      className="h-11 pl-10 text-[13.5px]"
                      {...field}
                    />
                  </div>
                </FormControl>
                {mode === "link" ? (
                  <FormDescription className="text-[12px]">
                    Debe matchear el correo con el que la usuaria entra al app móvil.
                  </FormDescription>
                ) : null}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Rol
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={rolesQuery.isLoading}
                >
                  <FormControl>
                    <SelectTrigger className="h-11 text-[13.5px]">
                      <SelectValue
                        placeholder={rolesQuery.isLoading ? "Cargando roles…" : "Elige un rol"}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {rolesQuery.data?.items.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium">{r.label}</p>
                          {r.description ? (
                            <p className="truncate text-[11.5px] text-muted-foreground">
                              {r.description}
                            </p>
                          ) : null}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password solo en modo "new" */}
          {mode === "new" ? (
            <FormField
              control={form.control}
              name={"password" as const}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                    Contraseña inicial
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock
                        aria-hidden
                        className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                      />
                      <Input
                        type={showPwd ? "text" : "password"}
                        autoComplete="new-password"
                        spellCheck={false}
                        className="h-11 pl-10 pr-11 text-[13.5px]"
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd((v) => !v)}
                        tabIndex={-1}
                        aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                        className={cn(
                          "absolute right-1.5 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground",
                          "hover:bg-accent/40 hover:text-foreground",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        )}
                      >
                        {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </FormControl>
                  <StrengthMeter score={strength.score} label={strength.label} />
                  <FormDescription className="text-[12px]">
                    12+ caracteres, con mayúsculas, minúsculas y un número.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <div className="rounded-xl border border-primary/15 bg-primary/[0.04] p-3.5">
              <div className="flex items-start gap-3">
                <Smartphone aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground">
                    Contraseña gestionada por la usuaria
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    La contraseña la maneja desde el app móvil. Al entrar al backoffice usará la
                    misma, y en el primer login deberá enrolar su TOTP.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Si el back dice can_link: ofrecer switch. Si no, error normal. */}
          {canLink && mode === "new" ? (
            <div className="flex animate-fade-in-up items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] p-3.5">
              <Sparkles
                aria-hidden
                className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300"
              />
              <div className="min-w-0 flex-1 space-y-2.5">
                <div>
                  <p className="text-[13px] font-medium text-foreground">
                    Esta cuenta ya existe en el app
                  </p>
                  <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                    Podemos vincularla al backoffice con el mismo correo. La contraseña la seguirá
                    manejando la usuaria desde el app.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => switchMode("link")}
                  className="border-amber-500/40 bg-background hover:bg-amber-500/5"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  Vincular cuenta del app
                </Button>
              </div>
            </div>
          ) : (
            <InlineError
              error={createState.error}
              fallback={
                mode === "link"
                  ? "No pudimos vincular la cuenta. Verifica que el correo exista en el app."
                  : "No pudimos crear el admin. Verifica que el email no esté en uso."
              }
            />
          )}
        </form>
      </Form>
    </FormSheet>
  );
}

/* ────────────────── Mode switch ────────────────── */

function ModeSwitch({ mode, onChange }: { mode: Mode; onChange: (next: Mode) => void }) {
  const options: { id: Mode; label: string; icon: React.ReactNode }[] = [
    { id: "new", label: "Nueva cuenta", icon: <UserPlus className="h-3.5 w-3.5" /> },
    { id: "link", label: "Vincular del app", icon: <Link2 className="h-3.5 w-3.5" /> },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Tipo de cuenta"
      className="grid grid-cols-2 gap-1 rounded-xl border bg-muted/40 p-1"
    >
      {options.map((opt) => {
        const selected = mode === opt.id;
        return (
          <button
            type="button"
            key={opt.id}
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.id)}
            className={cn(
              "inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-[12.5px] font-medium transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              selected
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {opt.icon}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────── Edit ────────────────── */

function EditSheet({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  user: AdminUser;
}) {
  const rolesQuery = useListRolesQuery(undefined, { skip: !open });
  const [updateAdmin, updateState] = useUpdateAdminUserMutation();
  const isLinked = user.account_kind === AccountKind.Linked;

  const form = useForm<AdminUserEdit>({
    resolver: zodResolver(AdminUserEditSchema),
    defaultValues: {
      full_name: user.full_name,
      role: user.role,
      active: user.active,
    },
    mode: "onBlur",
  });

  useEffect(() => {
    if (open) {
      form.reset({
        full_name: user.full_name,
        role: user.role,
        active: user.active,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user.id]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await updateAdmin({ id: user.id, body: values }).unwrap();
      toast.success("Admin actualizado");
      onOpenChange(false);
    } catch {
      // InlineError.
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Editar admin"
      description={
        isLinked
          ? "Esta cuenta está vinculada al app móvil. Puedes cambiar nombre, rol y estado desde acá."
          : "Email y contraseña se gestionan por otros flujos."
      }
      formId={FORM_ID}
      submitLabel="Guardar cambios"
      submitting={updateState.isLoading}
      dirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form id={FORM_ID} onSubmit={onSubmit} className="space-y-5" noValidate>
          {/* Email read-only + badge si es linked */}
          <div className="rounded-lg border bg-muted/40 p-3.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Correo
                </p>
                <p className="mt-1 truncate font-mono text-[13px] text-foreground">{user.email}</p>
              </div>
              {isLinked ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-primary">
                  <Link2 className="h-3 w-3" />
                  Vinculada
                </span>
              ) : null}
            </div>
          </div>

          {/* Banner informativo para cuentas vinculadas */}
          {isLinked ? (
            <div className="flex items-start gap-3 rounded-xl border border-primary/15 bg-primary/[0.04] p-3.5">
              <Smartphone aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">
                  Cuenta compartida con el app móvil
                </p>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                  La contraseña la gestiona la usuaria desde el app. Si la desactivas acá, pierde
                  solo el acceso al backoffice; sigue usando el app. Si borra su cuenta del app,
                  también pierde el backoffice.
                </p>
              </div>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="full_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Nombre completo
                </FormLabel>
                <FormControl>
                  <Input className="h-11 text-[13.5px]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  Rol
                </FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={rolesQuery.isLoading}
                >
                  <FormControl>
                    <SelectTrigger className="h-11 text-[13.5px]">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {rolesQuery.data?.items.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="active"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between rounded-xl border bg-background p-4">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-medium text-foreground">Cuenta activa</p>
                    <p className="text-[12px] text-muted-foreground">
                      {isLinked
                        ? "Si la desactivas, pierde el acceso al backoffice (no al app)."
                        : "Si la desactivas, el admin no podrá iniciar sesión."}
                    </p>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </div>
              </FormItem>
            )}
          />

          <InlineError error={updateState.error} fallback="No pudimos guardar los cambios." />
        </form>
      </Form>
    </FormSheet>
  );
}

/* ────────────────── Password strength meter ────────────────── */

function StrengthMeter({ score, label }: { score: 0 | 1 | 2 | 3 | 4; label: string }) {
  const colors = [
    "bg-destructive",
    "bg-destructive",
    "bg-amber-500",
    "bg-primary",
    "bg-emerald-500",
  ];
  const widths = ["20%", "40%", "60%", "80%", "100%"];
  return (
    <div className="flex items-center gap-2.5 pt-0.5">
      <div
        role="meter"
        aria-valuenow={score}
        aria-valuemin={0}
        aria-valuemax={4}
        aria-label="Fuerza de la contraseña"
        className="h-1 flex-1 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn("h-full rounded-full transition-all duration-200", colors[score])}
          style={{ width: widths[score] }}
        />
      </div>
      <span
        className={cn(
          "min-w-[70px] text-right font-mono text-[11px] uppercase tracking-wider",
          score < 2 ? "text-destructive" : score < 3 ? "text-amber-700" : "text-emerald-700",
        )}
      >
        {label}
      </span>
    </div>
  );
}
